import {
	type ChatCounterpart,
	DESK_COUNTERPART,
	type IOwnershipContext,
	type ISupportReplyContext,
} from '../../../interface/support-chat';
import { counterpartCopy } from '../../../lib/chat-counterpart-copy';
import { isOwnReply } from '../../../lib/support-api';

/**
 * When the quoted message is not from today, name the day as well as the time.
 *
 * <p>Consumer chat clients show only a name in a quote, because the message being answered is
 * usually seconds old. Here it routinely is not: the case this exists for is an instruction sent
 * on one day and answered on another, and the gap between them is the fact the agent needs.</p>
 */
const whenOf = (iso: string): string => {
	const at = new Date(iso);
	if (Number.isNaN(at.getTime())) return '';

	const time = at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	const sameDay = at.toDateString() === new Date().toDateString();
	return sameDay ? time : `${at.toLocaleDateString([], { day: 'numeric', month: 'short' })}, ${time}`;
};

const bodyOf = (reply: ISupportReplyContext): string => {
	if (reply.isDeleted) return 'Message deleted';
	if (reply.body) return reply.body;
	if (!reply.hasAttachments) return '';
	return reply.kind === 'document' ? 'PDF' : 'Photo';
};

interface IChatQuote {
	reply: ISupportReplyContext;
	/** `bubble` sits inside a sent message; `composer` sits above the input while a reply is armed. */
	variant: 'bubble' | 'composer';
	/** Who is reading — decides whether the header says "You". */
	ownership?: IOwnershipContext;
	counterpart?: ChatCounterpart;
}

/**
 * The quoted message, as a leading rule and two lines.
 *
 * <p>Rule only, no fill. Inside the green outgoing bubble no themed surface token has a defined
 * contrast relationship — the same reason `.sc-reattach__pick` reaches for a literal white
 * overlay there. `currentColor` has one on every surface by construction, in both themes.</p>
 *
 * <p>Which side is quoted rides on the header TEXT, not colour: this app already carries delivery
 * state in glyph count and fill for the same reason.</p>
 */
const ChatQuote = ({ reply, variant, ownership = { me: 0, kind: 'support' }, counterpart = DESK_COUNTERPART }: IChatQuote) => {
	// "Support desk", never the name of whichever admin replied — admins rotate through the desk
	// and the agent is talking to a function. The panel title makes the same choice. A peer is a
	// person and is named by their first name.
	const who = isOwnReply(reply, ownership) ? 'You' : counterpartCopy(counterpart).quoteName;
	const when = whenOf(reply.createdAt);

	return (
		<div className={`sc-quote sc-quote--${variant}`}>
			<span className='sc-quote__who'>
				{variant === 'composer' ? `Replying to ${who}` : who}
				{when ? <span className='sc-quote__when'> · {when}</span> : null}
			</span>
			<span className={reply.isDeleted ? 'sc-quote__body sc-quote__body--gone' : 'sc-quote__body'}>{bodyOf(reply)}</span>
		</div>
	);
};

export default ChatQuote;
